import React, { useState, useEffect } from 'react';
import { useShare } from '../context/ShareContext';
import { useDialog } from '../context/DialogContext';
import { HTMLService } from '../services/htmlService';
import { progressService } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { apiService, ShareDocument } from '../services/api';
import { SponsorService, Sponsor } from '../services/sponsorService';
import { supportsWebShareAPI } from '../utils/mobileDetection';
import styles from '../styles/shareModal.module.css';
import stepStyles from '../styles/stepCommon.module.css';

interface ShareModalProps {
  user?: any;
  isOpen?: boolean;
  onClose?: () => void;
  viewingForSponsee?: {
    userId: string;
    name: string;
    program: string;
  };
}

interface DocumentOption {
  value: string;
  label: string;
  description: string;
}

const SHARE_OPTIONS: DocumentOption[] = [
  {
    value: '12-step-status',
    label: 'SAA 12-Step Status',
    description: 'Your current progress on the 12 steps'
  },
  {
    value: 'step-four-inventories',
    label: 'SAA Step Four Inventories',
    description: 'Your 3 current inventories'
  }
];

type ViewState = 'select' | 'generating' | 'ready';

const ShareModal: React.FC<ShareModalProps> = ({ user, isOpen: isOpenProp, onClose: onCloseProp, viewingForSponsee }) => {
  const { isShareModalOpen, closeShareModal, shareModalOptions } = useShare();
  const { showAlert, showConfirm, showHelp } = useDialog();
  
  // Determine if modal is open (from context or prop)
  const isOpen = viewingForSponsee ? (isOpenProp ?? false) : isShareModalOpen;
  const handleClose = viewingForSponsee ? (onCloseProp || (() => {})) : closeShareModal;
  
  const [viewState, setViewState] = useState<ViewState>('select');
  const [currentDocument, setCurrentDocument] = useState<ShareDocument | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [justCopiedId, setJustCopiedId] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('');
  const [customEmail, setCustomEmail] = useState('');
  const [emailOption, setEmailOption] = useState<'sponsor' | 'custom'>('sponsor');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const supportsNativeShare = supportsWebShareAPI();

  // Auto-generate on open if specified in options
  useEffect(() => {
    if (isOpen && shareModalOptions?.autoGenerate) {
      const option = SHARE_OPTIONS.find(opt => opt.value === shareModalOptions.autoGenerate);
      if (option) {
        handleCreateOption(option.value, option.label);
      }
    }
  }, [isOpen, shareModalOptions]);

  // Load sponsors for email dropdown
  useEffect(() => {
    if (isOpen) {
      loadSponsors();
    }
  }, [isOpen]);

  const loadSponsors = async () => {
    try {
      const sponsorList = await SponsorService.getSponsors();
      setSponsors(sponsorList);
      if (sponsorList.length > 0) {
        setSelectedSponsorId(sponsorList[0].id);
        setEmailOption('sponsor');
      } else {
        // No sponsors, default to custom email
        setEmailOption('custom');
      }
    } catch (error) {
      console.error('Error loading sponsors:', error);
      setSponsors([]);
      setEmailOption('custom');
    }
  };

  const handleCreateOption = async (documentType: string, optionLabel: string) => {
    setViewState('generating');
    setIsGenerating(true);
    
    try {
      let htmlContent = '';
      let shareName = '';

      if (documentType === '12-step-status') {
        // Get all step progress
        const stepProgress = [];
        
        if (viewingForSponsee) {
          // Fetch sponsee's progress
          try {
            const response = await apiService.getSponseeStepProgress(viewingForSponsee.userId, viewingForSponsee.program);
            const progressItems = response.progress || [];
            
            // Convert to expected format
            for (let step = 1; step <= 12; step++) {
              const item = progressItems.find((p: any) => p.step_number === step);
              stepProgress.push({
                step_number: step,
                step_title: item?.step_title || `Step ${step}`,
                completed: item?.completed || false,
                data: item?.data || null
              });
            }
          } catch (error) {
            console.error('Error loading sponsee progress:', error);
            // Fill with empty steps
            for (let step = 1; step <= 12; step++) {
              stepProgress.push({
                step_number: step,
                step_title: `Step ${step}`,
                completed: false,
                data: null
              });
            }
          }
        } else {
          // Fetch current user's progress
          for (let step = 1; step <= 12; step++) {
            try {
              const progress = await progressService.getStepProgress('SAA', '12-steps', step);
              stepProgress.push({
                step_number: step,
                step_title: progress?.step_title || `Step ${step}`,
                completed: progress?.completed || false,
                data: progress?.data
              });
            } catch (error) {
              console.error(`Error loading step ${step} progress:`, error);
              stepProgress.push({
                step_number: step,
                step_title: `Step ${step}`,
                completed: false,
                data: null
              });
            }
          }
        }

        // Find the last step where any action has been completed
        let lastStepWithWork = 0;
        for (let i = stepProgress.length - 1; i >= 0; i--) {
          const step = stepProgress[i];
          // Check if step is completed OR has any completed actions
          if (step.completed) {
            lastStepWithWork = step.step_number;
            break;
          }
          if (step.data && step.data.actions) {
            const hasCompletedAction = step.data.actions.some((action: any) => action.completed);
            if (hasCompletedAction) {
              lastStepWithWork = step.step_number;
              break;
            }
          }
        }

        // Check if any steps have been started
        if (lastStepWithWork === 0) {
          const message = viewingForSponsee 
            ? `${viewingForSponsee.name} hasn't started working on SAA's 12 steps yet.`
            : 'There is nothing yet to share. Please come back when you have started working SAA\'s 12 steps.';
          showAlert({
            message,
            variant: 'info'
          });
          setViewState('select');
          setIsGenerating(false);
          return;
        }

        // Only include steps through the last step with work
        const filteredStepProgress = stepProgress.slice(0, lastStepWithWork);

        // Create user object for HTML generation
        const userForHtml = viewingForSponsee 
          ? { email: viewingForSponsee.userId, name: viewingForSponsee.name }
          : user;

        htmlContent = HTMLService.generate12StepStatusHTML(userForHtml, filteredStepProgress);
        shareName = 'SAA 12-Step Status';

      } else if (documentType === 'step-four-inventories') {
        let resentments, fears, harms;
        
        if (viewingForSponsee) {
          // Fetch sponsee's inventory data
          try {
            const response = await apiService.getSponseeInventories(viewingForSponsee.userId);
            resentments = response.resentments || [];
            fears = response.fears || [];
            harms = response.harms || [];
          } catch (error) {
            console.error('Error loading sponsee inventories:', error);
            resentments = [];
            fears = [];
            harms = [];
          }
        } else {
          // Get inventory data for current user (using InventoryService for decryption)
          [resentments, fears, harms] = await Promise.all([
            InventoryService.getInventory('resentments', 'resentments-current').catch(() => []),
            InventoryService.getInventory('fears', 'fears-current').catch(() => []),
            InventoryService.getInventory('harms', 'harms-current').catch(() => [])
          ]);
        }

        // Filter out completion status items
        const filteredResentments = resentments.filter((item: any) => item._type !== 'completion_status');
        const filteredFears = fears.filter((item: any) => item._type !== 'completion_status');
        const filteredHarms = harms.filter((item: any) => item._type !== 'completion_status');

        // Check if any inventories have data
        if (filteredResentments.length === 0 && filteredFears.length === 0 && filteredHarms.length === 0) {
          const message = viewingForSponsee 
            ? `${viewingForSponsee.name} hasn't started their Step Four inventories yet.`
            : 'There is nothing in your inventories yet. Please come back when you have started working on your Step Four inventories.';
          showAlert({
            message,
            variant: 'info'
          });
          setViewState('select');
          setIsGenerating(false);
          return;
        }

        // Create user object for HTML generation
        const userForHtml = viewingForSponsee 
          ? { email: viewingForSponsee.userId, name: viewingForSponsee.name }
          : user;
        
        htmlContent = HTMLService.generateStepFourInventoriesHTML(userForHtml, filteredResentments, filteredFears, filteredHarms);
        shareName = 'Step Four Inventories';
      }

      // Create share document
      const result = viewingForSponsee 
        ? await apiService.createSnapshotForSponsee(viewingForSponsee.userId, {
            share_name: shareName,
            document_type: documentType,
            html_content: htmlContent,
            program: viewingForSponsee.program
          })
        : await apiService.createShareDocument({
            share_name: shareName,
            document_type: documentType,
            html_content: htmlContent
          });

      // Create a ShareDocument object from the API response
      // Use short URL format instead of long S3 URL
      const shortUrl = `${window.location.origin}/s/${result.document_id}`;
      
      const shareDocument: ShareDocument = {
        id: result.document_id,
        user_id: viewingForSponsee ? viewingForSponsee.userId : (user?.email || ''),
        share_name: shareName,
        document_type: documentType,
        generation_date: new Date().toISOString(),
        share_url: shortUrl,
        expire_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US'),
        created_by_user_id: user?.email,
        created_by_name: user?.name,
        program: viewingForSponsee?.program
      };

      setCurrentDocument(shareDocument);
      setViewState('ready');

    } catch (error) {
      console.error('Error generating document:', error);
      showAlert({
        message: 'Error creating document. Please try again.',
        variant: 'danger'
      });
      setViewState('select');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOptionClick = (option: DocumentOption) => {
    // No confirmation needed - user clicked what they want, just create it
    handleCreateOption(option.value, option.label);
  };

  const handlePreview = () => {
    if (currentDocument) {
      window.open(currentDocument.share_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNativeShare = async () => {
    if (!currentDocument) return;
    
    if (navigator.share) {
      try {
        const userName = user?.name || user?.display_name || 'A Recovery Commons user';
        await navigator.share({
          title: currentDocument.share_name,
          text: `${userName} has shared their progress with you via Recovery Commons.\nView ${currentDocument.share_name}:`,
          url: currentDocument.share_url
        });
      } catch (error: any) {
        // User cancelled or error - silently handle
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
          showAlert({
            message: 'Share failed. Please try copy link instead.',
            variant: 'danger'
          });
        }
      }
    }
  };

  const handleCopyLink = async () => {
    if (!currentDocument) return;
    
    try {
      await navigator.clipboard.writeText(currentDocument.share_url);
      setJustCopiedId(currentDocument.id);
      setTimeout(() => setJustCopiedId(null), 3000);
    } catch (error) {
      showAlert({
        message: 'Failed to copy link. Please try again.',
        variant: 'danger'
      });
    }
  };

  const handleEmailClick = () => {
    setShowEmailForm(!showEmailForm);
    setCustomEmail('');
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const canSendEmail = (): boolean => {
    if (emailOption === 'sponsor') {
      return selectedSponsorId !== '';
    } else {
      return customEmail !== '' && isValidEmail(customEmail);
    }
  };

  const handleSendEmail = async () => {
    if (!currentDocument || !canSendEmail()) return;
    
    setIsSendingEmail(true);
    
    try {
      let recipientEmail = '';
      let recipientName = '';
      
      if (emailOption === 'sponsor' && selectedSponsorId && sponsors.length > 0) {
        const sponsor = sponsors.find(s => s.id === selectedSponsorId);
        if (sponsor) {
          recipientEmail = sponsor.email;
          recipientName = sponsor.name;
        }
      } else if (emailOption === 'custom' && customEmail) {
        recipientEmail = customEmail;
        recipientName = '';
      }

      if (!recipientEmail) {
        throw new Error('No recipient email');
      }

      // Send email via API
      await apiService.sendSnapshotEmail({
        document_id: currentDocument.id,
        recipient_email: recipientEmail,
        recipient_name: recipientName
      });

      // Show success and collapse form
      showAlert({
        message: `Successfully sent to ${recipientEmail}!`,
        variant: 'success'
      });
      
      setShowEmailForm(false);
      setCustomEmail('');

    } catch (error) {
      console.error('Error sending email:', error);
      showAlert({
        message: 'Failed to send email. Please try again.',
        variant: 'danger'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleBack = () => {
    setViewState('select');
    setCurrentDocument(null);
    setShowEmailForm(false);
    setCustomEmail('');
  };

  const handleModalClose = () => {
    setViewState('select');
    setCurrentDocument(null);
    setShowEmailForm(false);
    setCustomEmail('');
    setJustCopiedId(null);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleModalClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.titleSection}>
            <h2 className={styles.modalTitle}>
              {viewingForSponsee ? `${viewingForSponsee.name}'s Current Progress` : 'Share & Print'}
            </h2>
            <div className={styles.modalSubtitle}>
              {viewingForSponsee 
                ? `View and create reports for your sponsee (${viewingForSponsee.program})`
                : 'Share your current progress'
              }
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleModalClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* SELECT STATE - Choose what to share */}
          {viewState === 'select' && (
            <div className={styles.selectionSection}>
              <div className={styles.selectionHeader}>
                <h3 className={styles.selectionTitle}>
                  {viewingForSponsee ? 'What would you like to view?' : 'What would you like to share?'}
                </h3>
                <button 
                  className={stepStyles.helpBtn}
                  onClick={() => showHelp({
                    title: viewingForSponsee ? 'Viewing Sponsee Progress' : 'Share Your Progress',
                    content: viewingForSponsee 
                      ? `You can view ${viewingForSponsee.name}'s current progress. Reports are generated fresh each time and are only available for 7 days for privacy.`
                      : 'Create a report to view on screen, print, or send to your sponsor. Reports are only available for 7 days for your privacy.'
                  })}
                  tabIndex={-1}
                >
                  ?
                </button>
              </div>
              <div className={styles.optionsList}>
                {SHARE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    className={styles.optionCard}
                    onClick={() => handleOptionClick(option)}
                    disabled={isGenerating}
                  >
                    <div className={styles.optionHeader}>
                      <span className={styles.programBadge}>SAA</span>
                      <span className={styles.optionTitle}>
                        {option.value === '12-step-status' ? '12-Step Status' : 'Step Four Inventories'}
                      </span>
                    </div>
                    <span className={styles.optionDescription}>{option.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* GENERATING STATE */}
          {viewState === 'generating' && (
            <div className={styles.generatingContainer}>
              <div className={styles.spinner}></div>
              <p className={styles.generatingText}>Generating your report...</p>
              <p className={styles.generatingSubtext}>This may take a moment.</p>
            </div>
          )}

          {/* READY STATE - Share options */}
          {viewState === 'ready' && currentDocument && (
            <div className={styles.readyContainer}>
              <div className={styles.successMessage}>
                ✓ Your {currentDocument.share_name} report is ready!
              </div>

              <button 
                className={styles.previewButton}
                onClick={handlePreview}
              >
                👁️ Preview Report
              </button>

              <div className={styles.shareOptionsTitle}>How would you like to share it?</div>

              <div className={styles.shareButtonsGrid}>
                {supportsNativeShare && (
                  <button 
                    className={styles.shareButton}
                    onClick={handleNativeShare}
                  >
                    <span className={styles.shareIcon}>📱</span>
                    <span>Share</span>
                  </button>
                )}

                <button 
                  className={`${styles.shareButton} ${justCopiedId === currentDocument.id ? styles.copiedButton : ''}`}
                  onClick={handleCopyLink}
                >
                  <span className={styles.shareIcon}>
                    {justCopiedId === currentDocument.id ? '✓' : '📋'}
                  </span>
                  <span>{justCopiedId === currentDocument.id ? 'Copied!' : 'Copy Link'}</span>
                </button>

                <button 
                  className={`${styles.shareButton} ${showEmailForm ? styles.activeButton : ''}`}
                  onClick={handleEmailClick}
                >
                  <span className={styles.shareIcon}>✉️</span>
                  <span>Email</span>
                </button>
              </div>

              {/* Inline Email Form */}
              {showEmailForm && (
                <div className={styles.emailFormContainer}>
                  <h4 className={styles.emailFormTitle}>Send via Email</h4>
                  
                  {sponsors.length > 0 && (
                    <div className={styles.emailField}>
                      <label className={styles.emailRadioLabel}>
                        <input
                          type="radio"
                          name="emailOption"
                          checked={emailOption === 'sponsor'}
                          onChange={() => setEmailOption('sponsor')}
                          className={styles.emailRadio}
                        />
                        <span>Send to your sponsor:</span>
                      </label>
                      <select
                        value={selectedSponsorId}
                        onChange={(e) => {
                          setSelectedSponsorId(e.target.value);
                          setEmailOption('sponsor');
                        }}
                        className={styles.emailSelect}
                        disabled={sponsors.length === 0}
                      >
                        {sponsors.length === 0 && <option value="">No sponsors configured</option>}
                        {sponsors.length > 0 && <option value="">Select Sponsor</option>}
                        {sponsors.map(sponsor => (
                          <option key={sponsor.id} value={sponsor.id}>
                            {sponsor.name} ({sponsor.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className={styles.emailField}>
                    <label className={styles.emailRadioLabel}>
                      <input
                        type="radio"
                        name="emailOption"
                        checked={emailOption === 'custom'}
                        onChange={() => setEmailOption('custom')}
                        className={styles.emailRadio}
                        disabled={sponsors.length === 0}
                      />
                      <span>Send to another email:</span>
                    </label>
                    <input
                      type="email"
                      placeholder="email@example.com"
                      value={customEmail}
                      onChange={(e) => {
                        setCustomEmail(e.target.value);
                        // Auto-switch to custom when user starts typing
                        if (e.target.value.length > 0) {
                          setEmailOption('custom');
                        } else if (e.target.value.length === 0 && sponsors.length > 0) {
                          // Switch back to sponsor when field is cleared
                          setEmailOption('sponsor');
                        }
                      }}
                      className={styles.emailInput}
                    />
                    {customEmail && !isValidEmail(customEmail) && (
                      <div className={styles.validationError}>Please enter a valid email address</div>
                    )}
                  </div>

                  <div className={styles.emailFormButtons}>
                    <button 
                      className={styles.emailCancelButton}
                      onClick={() => {
                        setShowEmailForm(false);
                        setCustomEmail('');
                      }}
                      disabled={isSendingEmail}
                    >
                      Cancel
                    </button>
                    <button 
                      className={styles.emailSendButton}
                      onClick={handleSendEmail}
                      disabled={!canSendEmail() || isSendingEmail}
                    >
                      {isSendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              )}

              <button 
                className={styles.backButton}
                onClick={handleBack}
              >
                ← Back to choose different report
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
